import { meQueryOptions } from "@/api/auth";
import { PageInset } from "@/components/PageInset";
import { ButtonLink } from "@/components/ui/Link";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { TopAppBar } from "@/components/ui/TopAppBar";
import { AllowanceConversionNote } from "@/components/AllowanceConversionNote";
import { describeAllowanceBalance, formatEapRepaymentCap, useMemberBenefits } from "@/hooks/useMemberBenefits";
import { formatDollars } from "@common/utils/i18n";
import { Box, Flex, Grid, Text } from "@givenwell/components";
import { styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ComponentProps } from "react";

export const Route = createFileRoute("/_auth/_app/home")({
  component: RouteComponent,
});

function RouteComponent() {
  const { eap, allowance } = useMemberBenefits();
  const { balance, conversionNote, conversionHelp } = describeAllowanceBalance(allowance);

  return (
    <PageWrapper topAppBar={<TopAppBar />}>
      <PageInset css={{ minHeight: "100%", display: "flex", flexDirection: "column" }}>
        <Flex
          css={{ spaceY: "var(--layout-margin)", flexDirection: "column", flex: "1 0 0px", maxWidth: 2100, mx: "auto" }}
        >
          {/* <IntroCard /> */}
          <Grid css={{ gridCols: 1, gap: "var(--layout-margin)", "@sm": { gridCols: 2 }, flex: "1 0 0px" }}>
            {eap?.canSubmitClaims && (
              <Card css={{ backgroundColor: colors.green }}>
                <Blob fill={colors.greenAccent} />
                <Overline>Funded Sessions:</Overline>
                <Title>
                  You have <Highlight>{eap?.availableSessions} sessions</Highlight> available to claim.
                </Title>
                <Text>
                  Upload a receipt from a session with a wellbeing provider of your choice, and we'll replay you up to{" "}
                  {formatEapRepaymentCap(eap)} per session into your bank account.
                </Text>
                <Buttons>
                  <ButtonLink
                    variant="filled"
                    color="pink"
                    to="/claims/new"
                    search={{ eap_supporter_id: eap?.supporterId }}
                  >
                    Claim back for a session
                  </ButtonLink>
                  <ButtonLink variant="filled" color="neutralLight" to="/claims/policy/eap">
                    See what you can claim
                  </ButtonLink>
                </Buttons>
              </Card>
            )}
            <Card css={{ backgroundColor: colors.cyan }}>
              <Blob fill={colors.cyanAccent} />
              <Overline>Wellbeing Allowance:</Overline>
              <Box css={{ spaceY: 8 }}>
                <Title>
                  You have <Highlight>{balance}</Highlight> available to claim.
                </Title>
                {conversionNote && conversionHelp && (
                  <AllowanceConversionNote note={conversionNote} help={conversionHelp} />
                )}
              </Box>
              <Text>
                Upload a receipt for something you've spent on for your wellbeing, and we'll pay you back into your bank
                account.
              </Text>
              <Buttons>
                <ButtonLink variant="filled" color="pink" to="/claims/new">
                  Create new claim
                </ButtonLink>
                <ButtonLink variant="filled" color="neutralLight" to="/claims/policy/allowance">
                  See what you can claim
                </ButtonLink>
              </Buttons>
            </Card>
          </Grid>
        </Flex>
      </PageInset>
    </PageWrapper>
  );
}

function IntroCard() {
  const { allowance } = useMemberBenefits();
  const { data: user } = useQuery(meQueryOptions);
  const { balance, conversionNote, conversionHelp } = describeAllowanceBalance(allowance);

  return (
    <Box
      css={{ backgroundColor: colors.green, padding: 8, borderRadius: 16, overflow: "hidden", position: "relative" }}
    >
      <Blob
        fill={colors.greenAccent}
        style={{
          top: "unset",
          right: "unset",
        }}
      />
      <Box css={{ padding: 8, spaceY: 12, position: "relative" }}>
        <Text css={{ fontFamily: fonts.ftHabit, fontScale: "2xl", weight: 600, lineHeight: 1.1 }}>
          Hi {user?.given_name}!
        </Text>
        <Text css={{ maxWidth: "80ch" }}>
          You have {balance} to spend on wellbeing support you choose. You can either spend on one of our partner offers
          below, or upload a receipt for something else and we'll pay you back.
        </Text>
        {conversionNote && conversionHelp && <AllowanceConversionNote note={conversionNote} help={conversionHelp} />}
      </Box>
    </Box>
  );
}

const Card = styled("div", {
  padding: 16,
  backgroundColor: colors.green,
  borderRadius: 16,
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: 12,
  overflow: "hidden",
  position: "relative",
  "&>*": {
    position: "relative",
  },
});

const Overline = styled("div", {
  fontScale: "xl",
  weight: 600,
  fontFamily: fonts.ftHabit,
});

const Title = styled("div", {
  fontScale: "3xl",
  lineHeight: 1.1,
  weight: 600,
  fontFamily: fonts.ftHabit,
  textWrap: "pretty",
});

const Highlight = styled("span", {
  backgroundColor: colors.white,
  borderRadius: 6,
  px: 4,
});

const Buttons = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: 12,
  maxWidth: 300,
  width: "100%",
  mx: "auto",
});

function Blob({ fill, ...props }: ComponentProps<"svg"> & { fill: string }) {
  return (
    <svg
      width="412"
      height="723"
      viewBox="0 0 412 723"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      preserveAspectRatio="xMidYMid slice"
      {...props}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        ...props.style,
      }}
    >
      <path
        d="M-493.801 155.054C-344.986 -41.7923 19.7266 -90.438 196.728 243.936C356.498 545.745 497.183 848.792 348.389 1045.61C199.594 1242.43 45.3354 1082.9 -238.815 927.212C-511.119 778.045 -642.616 351.9 -493.801 155.054Z"
        fill={fill}
      />
    </svg>
  );
}
